import { Performer } from 'globals/store/performers.data'
import { Sponsor, sponsorStatus } from 'globals/store/sponsors.data'
import { Session, Program, DropdownOption, SessionContentStatus } from 'globals/store/programs.data'
import { tableLightColors } from 'src/app/helpers/table-helpers'
import { publicEditProfileStatusTypes, ItemStatus } from 'globals/store/share.data'
import { colorsByPublicEditProfile, performerStatus, briefStatus, tableFilterTypes } from '../performers.era'
import { createSelector } from '@ngrx/store'
import { FilterGroup } from 'globals/store/filters.data'
import { Dictionary } from '@ngrx/entity'
import { jsonCopyObject, str2Date, time_to_min, makeMapFromListOfObjectsAndKeyToProperty, getList, sortByProp } from 'globals/helpers/helpers'
import { programsArray } from './programs.reselector'
import { DAY_MS } from 'globals/helpers/constants'
import { JoiComment } from 'globals/store/tasks.data'
import { TableLight, TableLights, TableLightsWithURLForExport } from 'src/app/helpers/table.data'
import { addACommaToLights } from './entity.decircular.reselector'
import { sponsorsStateSelector } from '../sponsors.era'
export { addACommaToLights }

export const SearchableSessionFields = ['title', 'programTitle', 'description', 'start', 'end']

const performerEntities = (store) => store.performers.entities
const performerTableFilters = (store) => store.filters.performersTable
const sessionTableFilters = (store) => store.filters.performerSessionsTable
const performerFilterType = (store) => store.performers.performerFilterType

const sponsorEntities = createSelector(sponsorsStateSelector, (store) => store.entities)
const sponsorTableFilters = (store) => store.filters.sponsors
const sponsorSessionTableFilters = (store) => store.filters.sponsor_sessions

export interface ProgramsSponsors {
  programsList: Program[]
  sponsorList: Sponsor[]
}


export interface SessionSponsorInfo {
  sponsorId: string
  status: string
}

export interface SessionSponsorData {
  sessionList: SponsorSessionInfo[]
  sessionProgramList: NestedProgramInfo[]
}

export interface SponsorSessionInfoLite {
  sessionId: string
  status: string
}

export interface SponsorSessionInfo extends SessionInfo {
  status: string
}

export interface PerformerSessionInfoLite {
  sessionId: string
  status: string
  briefStatus: string
}

export interface SessionPerformerInfo {
  performerId: string
  status: string
  briefStatus: string
}

export interface SessionInfo {
  programId: string
  id: string
  title: string
  programTitle: string
  start: string
  end: string
  date: Date
  day: string
  duration?: number
  selected?: boolean // only for selector dialog
  performerInfos: SessionPerformerInfo[] // filtered for currently existing performers
  performerStatus: { [performerId: string]: string }
  performerBriefStatus: { [performerId: string]: string }
  contentStatus: SessionContentStatus
  sessionLabels: string[]
  programLabels: string[]
  description: string
  performers?: Performer[] // for use in the manage performer session dialog
  performerIds?: string[]
  sponsors?: Sponsor[] // for use in the manage sponsor session dialog
  sponsorStatus: { [sponsorId: string]: string }
  sponsorInfos: SessionSponsorInfo[]
  unusedPerformers?: Performer[]
  unusedSponsors?: Sponsor[] 
  fullSession?: Session
  quickNotes?: JoiComment[]
  flag?: boolean
  attendeeTags: string[]

  dropdownSelection?: DropdownOption
  dropdownSelections?: DropdownOption[]

}

export interface ProgramsPerformersSponsors {
  programsList: Program[]
  performerList: Performer[]
  sponsorList: Sponsor[]
}

export interface PerformerSessionInfo extends SessionInfo {
  status: string
  briefStatus: string
}

export interface ExhibitorStandSelection {
  id: string
  name: string
  selected: boolean
  disabled: boolean
  message: string
}

export interface NestedProgramInfo { programId: string, programTitle: string, sessionList?: SessionInfo[], standList?: ExhibitorStandSelection[] }
export interface SessionPerformerData {
  sessionList: PerformerSessionInfo[]
  sessionProgramList: NestedProgramInfo[]
}

export interface SessionsEntitiesData {
  sessionInfos: { [sId: string]: SessionInfo }
  performerSessionInfosByPerformerId: { [pId: string]: PerformerSessionInfoLite[] }
  unusedSessionIdsByPerformerId: { [pId: string]: string[] }
  performers: { [id: string]: Performer }
  sponsors: { [id: string]: Sponsor },
  sponsorSessionInfosBySponsorId: { [sId: string]: SponsorSessionInfoLite[] }
  unusedSessionIdsBySponsorId: { [sId: string]: string[] }
}


export const sortByDateAndStart = (a, b) => !a.date ? -1 : !b.date ? 1 : a.date === b.date ? (a.start < b.start ? -1 : 1) : a.date < b.date ? -1 : 1
// For use in manage performer for both listing performer sessions as well as listing 'unused' sessions

export const getBooleanLightWrapper = (trueText, falseText, trueColor, falseColor) => boolean => ({ percent: boolean ? 1 : 0, lights: [{ text: boolean ? trueText : falseText, color: boolean ? trueColor : falseColor }] })
export const getBooleanLight = getBooleanLightWrapper('Yes', 'No', tableLightColors.green, tableLightColors.red)
export const getBooleanConfirmedLight = getBooleanLightWrapper('Confirmed', 'Not Confirmed', tableLightColors.green, tableLightColors.gray)

export const getActiveLights = (editStatus, withUrlCode?: string) => {
  const text = editStatus ? editStatus : publicEditProfileStatusTypes.Off;
  const percent = editStatus === publicEditProfileStatusTypes.Submitted ? 1 : (publicEditProfileStatusTypes.Submitted ? .5 : 0)
  const status:TableLightsWithURLForExport = { percent, lights:[{ text, color: colorsByPublicEditProfile[text] }] }
  if (withUrlCode && editStatus === publicEditProfileStatusTypes.Active) status.url = withUrlCode;
  return status;
}

export const getStatusLights = (infos: SessionPerformerInfo[] | PerformerSessionInfoLite[], statusName) => {
  const acceptedCount = statusCount(infos, performerStatus.Accepted)
  const percent = !infos.length ? -1 : acceptedCount / infos.length
  const pendingCount = statusCount(infos, performerStatus.Pending)
  const rejectedCount = statusCount(infos, performerStatus.Declined)
  const uninvitedCount = !infos.length ? 0 : infos.length - acceptedCount - pendingCount - rejectedCount // "NotInvited" can be set explicitly, but the status is also "NotInvited" if there is no status

  const status = !infos.length ? { percent, greyText: `No ${statusName}` } : {
    percent, lights: [
      !pendingCount ? null : { text: `${pendingCount} pending`, color: tableLightColors.yellow },
      !acceptedCount ? null : { text: `${acceptedCount} accepted`, color: tableLightColors.green },
      !rejectedCount ? null : { text: `${rejectedCount} declined`, color: tableLightColors.red },
      !uninvitedCount ? null : { text: `${uninvitedCount} not invited`, color: tableLightColors.gray },
    ].filter(l => l)
  }
  status.lights && addACommaToLights(status.lights, 0)
  return status
}

export const getSponsorStatusLights = (infos: SponsorSessionInfoLite[] | SessionSponsorInfo[], statusName) => {
  const acceptedCount = statusCount(infos, sponsorStatus.Confirmed)
  const percent = !infos.length ? -1 : acceptedCount / infos.length
  const pendingCount = statusCount(infos, sponsorStatus.Pending)
  const rejectedCount = statusCount(infos, sponsorStatus.Cancelled)

  const status = !infos.length ? { percent, greyText: `No ${statusName}` } : {
    percent, lights: [
      !pendingCount ? null : { text: `${pendingCount} pending`, color: tableLightColors.yellow },
      !acceptedCount ? null : { text: `${acceptedCount} confirmed`, color: tableLightColors.green },
      !rejectedCount ? null : { text: `${rejectedCount} cancelled`, color: tableLightColors.red },
    ].filter(l => l)
  }
  status.lights && addACommaToLights(status.lights, 0)
  return status
}


const statusCount = (infos, status) => infos.reduce((acc, info) => acc + (info && info.status === status ? 1 : 0), 0)
const briefStatusCount = (infos, status) => infos.reduce((acc, info) => acc + (info && info.briefStatus === status ? 1 : 0), 0)



const stringCount = (strings: string[], match: string): number => !strings ? 0 : strings.filter(s => s === match).length

// "Off" | "Active" | "Submitted"

export const getAssetLights = (assetStatus: ItemStatus[]): TableLights => {
  const activeCount = stringCount(assetStatus, 'Active')
  const submittedCount = stringCount(assetStatus, 'Submitted')
  const percent = !assetStatus || !assetStatus.length ? -1 : submittedCount / assetStatus.length
  const offCount = !assetStatus ? 0 : assetStatus.length - activeCount - submittedCount
  const status = !assetStatus || !assetStatus.length ? { percent, lights: [{ text: '1 off', color: tableLightColors.gray}] } : {
    percent, lights: [
      !activeCount ? null : { text: `${activeCount} active`, color: tableLightColors.yellow },
      !submittedCount ? null : { text: `${submittedCount} submitted`, color: tableLightColors.green },
      !offCount ? null : { text: `${offCount} off`, color: tableLightColors.gray },
    ].filter(l => l)
  }
  status.lights && addACommaToLights(status.lights, 0)
  return status
}

export const getBriefLights = (infos: SessionPerformerInfo[] | PerformerSessionInfoLite[], parentType: string = 'performer') => {
  const acceptedCount = briefStatusCount(infos, briefStatus.Briefed)
  const percent = !infos.length ? -1 : acceptedCount / infos.length
  const rejectedCount = infos.length - acceptedCount

  const status = !infos.length ? { percent, greyText: `No ${parentType}s` } : {
    percent, lights: [
      !acceptedCount ? null : { text: `${acceptedCount} briefed`, color: tableLightColors.green },
      !rejectedCount ? null : { text: `${rejectedCount} not briefed`, color: tableLightColors.gray },
    ].filter(l => l)
  }
  status.lights && addACommaToLights(status.lights, 0)
  return status
}



const filterProgramsSessions = (programsList: Program[], filters: FilterGroup) => {
  programsList = jsonCopyObject(programsList)
  programsList = programsList.filter(p => ![
    () => !filters.programs || filters.programs.includes(p.id),
    () => !filters.programLabels || (p.labels && p.labels.some(l => filters.programLabels.includes(l))),
  ].some(passesFilterFunction => !passesFilterFunction()))
  programsList.forEach(p => {
    const usedIds: Set<string> = new Set()
    p.sessions.forEach(day => {
      day.sessions = day.sessions.filter(s =>
        !filters.sessionLabels || (p.sessions_obj[s].labels && p.sessions_obj[s].labels.some(l => filters.sessionLabels.includes(l))))
      day.sessions.forEach(s => usedIds.add(s))
    })
    const filteredSessions = {}
    usedIds.forEach(id => filteredSessions[id] = p.sessions_obj[id])
    p.sessions_obj = filteredSessions
  })
  return programsList
}

export const filterProgramsSessionsBySponsors = (sponsors: Dictionary<Sponsor>, programsList: Program[], filters: FilterGroup): ProgramsSponsors => {
  programsList = filterProgramsSessions(programsList, filters)
  // console.log({ filters, sponsors })
  const sponsorList = Object.values(sponsors).filter(s => ![
    () => !filters.tags || filters.tags.includes(s.WFContact),
    () => !filters.sponsorLabels || (s.labels && s.labels.some(l => filters.sponsorLabels.includes(l))),
  ].some(passesFilterFunction => !passesFilterFunction()))
  return { programsList, sponsorList }
}

export const filterProgramsSessionsByPerformers = (performers: Dictionary<Performer>, sponsors: Dictionary<Sponsor>, programsList: Program[], filters: FilterGroup): ProgramsPerformersSponsors => {
  programsList = filterProgramsSessions(programsList, filters)
  const performerList = Object.values(performers).filter(p => ![
    () => !filters.tags || filters.tags.includes(p.contact),
    () => !filters.performerLabels || (p.labels && p.labels.some(l => filters.performerLabels.includes(l))),
  ].some(passesFilterFunction => !passesFilterFunction()))
  const sponsorList = Object.values(sponsors).filter(s => ![
    () => !filters.tags || filters.tags.includes(s.WFContact),
    () => !filters.sponsorLabels || (s.labels && s.labels.some(l => filters.sponsorLabels.includes(l))),
  ].some(passesFilterFunction => !passesFilterFunction()))
  return { programsList, performerList, sponsorList }
}


export const filteredSponsorsProgramsReselector = createSelector(
  sponsorEntities,
  programsArray,
  sponsorTableFilters,
  sponsorSessionTableFilters,
  performerFilterType, // <== YES, THIS IS IN THE WRONG PLACE, KIND OF.  BUT EASIER TO LEAVE IT HERE
  (sponsors, programsList, sponsorTableFilters, sponsorSessionTableFilters, filterType): ProgramsSponsors => {
    // if (!filterType) return { programsList: [], sponsorList: [] }
    let filters: FilterGroup
    // console.log(`filteredPerformersProgramsReselector called with filterType: [${filterType}]`)
    // console.log({ filterType })
    switch (filterType) {
      case tableFilterTypes.SponsorsTable:
        filters = sponsorTableFilters
        break
      case tableFilterTypes.SponsorSessionsTable:
        filters = sponsorSessionTableFilters
        break
      default:
        // console.log("NO FILTERS!")
        return { programsList, sponsorList: Object.values(sponsors) }
    }
    return filterProgramsSessionsBySponsors(sponsors, programsList, filters)
  }
)


export const filteredPerformersProgramsReselector = createSelector(
  performerEntities,
  sponsorEntities,
  programsArray,
  performerTableFilters,
  sessionTableFilters,
  performerFilterType,
  (performers, sponsors, programsList, performerTableFilters, sessionTableFilters, filterType): ProgramsPerformersSponsors => {
    if (!filterType) return { programsList: [], performerList: [], sponsorList: [] }
    let filters: FilterGroup
    // console.log(`filteredPerformersProgramsReselector called with filterType: [${filterType}]`, sessionTableFilters)
    switch (filterType) {
      case tableFilterTypes.PerformersTable:
        filters = performerTableFilters
        break
      case tableFilterTypes.SessionsTable:
        filters = sessionTableFilters
        break
      default:
        const performerList = Object.values(performers)
        const sponsorList = Object.values(sponsors)
        return { programsList, performerList, sponsorList }
    }
    return filterProgramsSessionsByPerformers(performers, sponsors, programsList, filters)
  }
)


const sessionEntitiesExtractorWrapper = (
  performerEntities: {[pId:string]: Performer}, 
  { programsList, performerList }: ProgramsPerformersSponsors, 
  sponsorEntities: {[sId:string]: Sponsor},
  { programsList: sponsorProgramsList, sponsorList }: ProgramsSponsors,  
  filterType: string,
): SessionsEntitiesData => sessionsEntitiesExtractor(performerEntities, programsList, sponsorEntities, { programsList: sponsorProgramsList, sponsorList }, filterType)

const sessionsEntitiesExtractor = (
  performerEntities: {[pId:string]: Performer}, 
  programsList: Program[],
  sponsorEntities: {[sId:string]: Sponsor},
  { programsList: sponsorProgramsList, sponsorList }: ProgramsSponsors,  
  filterType: string,
  sessionId: string = null,
): SessionsEntitiesData => {

  // console.log({ sessionId })
  if (filterType && filterType.includes('Sponsor')) programsList = sponsorProgramsList
  // console.log({ programsList })
  const sessionInfos: { [sId: string]: SessionInfo } = {}
  const performerSessionInfosByPerformerId: { [pId: string]: PerformerSessionInfoLite[] } = {}
  const unusedSessionIdsByPerformerId: { [pId: string]: string[] } = {}


  const performerList = Object.values(performerEntities).sort(sortByProp('name'))
  const allPerformerIds = performerList.map(perf=>perf.id);
  performerList.forEach(({ id }) => {
    performerSessionInfosByPerformerId[id] = []
    unusedSessionIdsByPerformerId[id] = []
  })

  const sponsorSessionInfosBySponsorId: { [sId: string]: SponsorSessionInfoLite[] } = {}
  const unusedSessionIdsBySponsorId: { [sId: string]: string[] } = {} 

  sponsorList.forEach(({ id }) => {
    sponsorSessionInfosBySponsorId[id] = []
    unusedSessionIdsBySponsorId[id] = []
  })

  const sponsorIdList = getList(sponsorList, 'id')

  // console.log({ sponsorEntities, sponsorList, sponsorIdSet })
  if(programsList){
    programsList.forEach(p => Object.values(p.sessions_obj).filter(s => s.type === 'session').forEach(s => {
      const sessionPerformers: SessionPerformerInfo[] = []

      const { id: programId, title: programTitle, date: firstDay, labels: programLabels } = p
      const { id, title, on_day, start, end, duration, labels: sessionLabels, performerIds, description, quickNotes, flag, contentStatus } = s

      allPerformerIds.forEach(performerId => { // need to iterate over performerIds instead of session.performerIds as there may deleted performers lingering there
        if (s.performerIds && s.performerIds.includes(performerId)) {
          const status = s.performerStatus[performerId] || performerStatus.NotInvited
          const bStatus = s.performerBriefStatus[performerId] || 'Off'
          sessionPerformers.push({ performerId, status, briefStatus: bStatus })
          if (!performerSessionInfosByPerformerId[performerId]) performerSessionInfosByPerformerId[performerId] = []
          performerSessionInfosByPerformerId[performerId].push({ sessionId: s.id, status, briefStatus: bStatus })
        } else {
          unusedSessionIdsByPerformerId[performerId].push(s.id)
        }
      })

      const sessionSponsors: SessionSponsorInfo[] = []
      sponsorIdList.forEach(sponsorId => { 
        if (s.sponsorIds && s.sponsorIds.includes(sponsorId)) {
          const status = s.sponsorStatus[sponsorId] || sponsorStatus.Pending
          sessionSponsors.push({ sponsorId, status })
          sponsorSessionInfosBySponsorId[sponsorId].push({ sessionId: s.id, status })
        } else {
          unusedSessionIdsBySponsorId[sponsorId].push(s.id)
        }
      })

      const day = !firstDay ? `Day ${on_day}` : null
      const date = firstDay ? new Date(str2Date(firstDay).getTime() + (+on_day - 1) * DAY_MS + time_to_min(start) * 60000) : new Date((+on_day*2400 + +s.start.split(":").join(""))*60000) // this last part is for sorting only  
      sessionInfos[id] = {
        programId,
        id,
        title,
        programTitle,
        start,
        end,
        date,
        day,
        duration,
        performerIds,
        performerInfos: sessionPerformers,
        sponsorInfos: sessionSponsors,
        sponsorStatus: makeMapFromListOfObjectsAndKeyToProperty(sessionSponsors, 'sponsorId', 'status'),
        performerStatus: makeMapFromListOfObjectsAndKeyToProperty(sessionPerformers, 'performerId', 'status'),
        performerBriefStatus: makeMapFromListOfObjectsAndKeyToProperty(sessionPerformers, 'performerId', 'briefStatus'),
        sessionLabels,
        programLabels,
        description,
        quickNotes,
        flag,
        contentStatus,
        fullSession: jsonCopyObject(s),
        attendeeTags: s.attendeeTags || []
      }

    }))
  }

  return { 
    sessionInfos, 
    performerSessionInfosByPerformerId, 
    unusedSessionIdsByPerformerId, 
    performers: performerEntities, 
    sponsors: sponsorEntities,
    sponsorSessionInfosBySponsorId,
    unusedSessionIdsBySponsorId 
  }
}

export const fullSponsorsProgramsReselector = createSelector(
  sponsorEntities,
  programsArray,
  (sponsors, programsList): ProgramsSponsors => ({ sponsorList: Object.values(sponsors), programsList: jsonCopyObject(programsList) })
)


export const sessionsEntitiesReselector = createSelector(
  performerEntities,
  filteredPerformersProgramsReselector,
  sponsorEntities,
  filteredSponsorsProgramsReselector,
  performerFilterType,
  sessionEntitiesExtractorWrapper
)

export const fullSessionsEntitiesReselector = createSelector(
  performerEntities,
  programsArray,
  sponsorEntities,
  fullSponsorsProgramsReselector,
  performerFilterType,
  sessionsEntitiesExtractor
)
